import type { StrictColumnDef } from '../../../components/tanstack-table/column';
import type { OrderOptions } from '../../../data/order-options';
import type { Product } from '../../../data/product';
import { hasPricingData, isActiveProduct, saleStat } from '../../../data/product';
import type { Vendor, VendorIntegration } from '../../../data/vendor';
import type { ProductOrderItem } from './data/product-order-item';
import type { Quantities } from './types';

export function underThreshold(product: Product, qoh_threshold_weeks: number) {
  return product.inventory_qty < (saleStat(product, 30) / 30) * 7 * qoh_threshold_weeks;
}

export function stockBelowOneItem(product: Product) {
  if (product.last_sold === null) {
    return false;
  }

  return product.standard_qty > 1 && product.inventory_qty < product.standard_qty + 1;
}

export function minQOH(product: Product) {
  return product.min_qoh && product.inventory_qty < product.min_qoh;
}

export function antiRolloff(product: Product) {
  return product.inventory_qty <= 0 && saleStat(product, 90) > 0;
}

export function getSuggestedQuantities(product: Product, options: OrderOptions): Quantities {
  if (product.disabled || product.auto_disabled || options.reorder_weeks === 0) {
    return {
      suggested_units: 0,
      suggested_cases: 0,
    };
  }

  const sales = saleStat(product, options.sales_period_days);

  const sales_units =
    underThreshold(product, options.qoh_threshold_weeks) || stockBelowOneItem(product)
      ? Math.max(1, Math.round((sales / options.sales_period_days) * 7 * options.reorder_weeks))
      : 0;
  if (product.min_qoh === undefined) {
    throw 'Product min_qoh is undefined';
  }

  const min_qoh_units = minQOH(product) ? product.min_qoh - product.inventory_qty : 0;
  const anti_rolloff_units = antiRolloff(product) ? 1 : 0;

  const suggested_total_units = Math.max(sales_units, min_qoh_units, anti_rolloff_units);

  if (suggested_total_units === 0) {
    return {
      suggested_units: 0,
      suggested_cases: 0,
    };
  }

  if (!product.case_qty) {
    return {
      suggested_units: suggested_total_units,
      suggested_cases: 0,
    };
  }

  let suggested_cases = Math.floor(suggested_total_units / product.case_qty);
  let suggested_units = suggested_total_units % product.case_qty;

  if (suggested_cases <= 0) {
    // Minimum case order is 1. Round up 2/3 of a case or more of units to 1 case
    if (
      product.order_lot === 'C' ||
      (product.order_lot === 'U' && suggested_units > 0.66 * product.case_qty)
    ) {
      suggested_cases = 1;
      suggested_units = 0;
    }
  } else {
    // Round up 1/2 of a case or more of units to the next case
    if (suggested_units > 0.5 * product.case_qty) {
      suggested_cases += 1;
      suggested_units = 0;
    }
  }

  if (product.order_lot === 'C') {
    suggested_units = 0;
  }

  return {
    suggested_units,
    suggested_cases,
  };
}

/**
 * Wraps a Product column definition to work with ProductOrderItem.
 * This allows reusing existing Product columns in order editor tables.
 */
export function wrapProductColumn(
  column: StrictColumnDef<Product>
): StrictColumnDef<ProductOrderItem> {
  // Wrap the sorting function to work with ProductOrderItem
  // Capture in a const so TypeScript narrows the type inside the closure
  const productSortingFn = column.sortingFn;
  const sortingFn: StrictColumnDef<ProductOrderItem>['sortingFn'] =
    productSortingFn !== undefined
      ? (rowA, rowB, columnId) => {
          return productSortingFn(
            { original: rowA.original.product },
            { original: rowB.original.product },
            columnId
          );
        }
      : undefined;

  const productCell = column.cell;
  const cell: StrictColumnDef<ProductOrderItem>['cell'] =
    productCell !== undefined
      ? ({ row, getValue, table }) => {
          return productCell({
            row: { original: row.original.product, id: row.id },
            getValue,
            table,
          });
        }
      : undefined;

  const productToCSV = column.meta.toCSV;
  const toCSV =
    productToCSV !== undefined
      ? (orderItem: ProductOrderItem) => productToCSV(orderItem.product)
      : undefined;

  return {
    id: column.id,
    header: column.header,
    accessorFn: (orderItem) => column.accessorFn(orderItem.product),
    cell,
    sortingFn,
    enableSorting: column.enableSorting,
    minSize: column.minSize,
    maxSize: column.maxSize,
    size: column.size,
    meta: {
      ...column.meta,
      toString: (orderItem) => column.meta.toString(orderItem.product),
      toCSV,
    },
  };
}

export function matchesSize(product: Product, sizes: string[]) {
  if (sizes.length === 0) {
    return true;
  }

  return sizes.includes(product.size);
}

/**
 * Buyup membership test. With `includeInactive=false` (default), inactive
 * products are filtered out — set it to true when the caller renders its
 * own dim/badge treatment for inactive rows.
 */
export function matchesIsBuyup(product: Product, isBuyup: boolean, includeInactive = false) {
  if (!isBuyup) {
    return true;
  }

  if (!includeInactive && !isActiveProduct(product)) {
    return false;
  }

  return hasPricingData(product);
}

export function vendorHasVolumePricing(vendor: Vendor, integrations: VendorIntegration[]): boolean {
  if (!vendor.normalized_name) {
    return false;
  }

  const integ = integrations.find((i) => i.normalized_name === vendor.normalized_name);

  return integ?.pricing?.has_volume_pricing === true;
}
